/**
 * openScope route strings and their expansion into fix legs.
 *
 *   "AMAKR.BDEGA3.KSFO28L"        arrival: entry fix . STAR . runway
 *   "KSFO28L.GNNRR2.AMAKR"        departure: runway . SID . exit fix
 *   "FIX1..FIX2..ENTRY.STAR.RWY"  ".." separates direct segments from procedure segments
 */
import type { AirportData, Procedure, RouteLeg } from './airport';

export type RouteSegment =
  | { kind: 'direct'; fix: string }
  | { kind: 'procedure'; entry: string; procedure: string; exit: string };

export function parseRouteString(route: string): RouteSegment[] {
  const out: RouteSegment[] = [];
  for (const part of route.trim().toUpperCase().split('..')) {
    if (!part) continue;
    const bits = part.split('.');
    if (bits.length === 3) out.push({ kind: 'procedure', entry: bits[0]!, procedure: bits[1]!, exit: bits[2]! });
    else if (bits.length === 1) out.push({ kind: 'direct', fix: bits[0]! });
    else throw new Error(`Unparseable route segment "${part}" in "${route}"`);
  }
  return out;
}

export interface ExpandedRoute {
  legs: RouteLeg[];
  /** Procedure used, if any. */
  procedure?: Procedure;
  /** For a SID the exit fix, for a STAR the runway key (e.g. "KSFO28L"). */
  exit?: string;
  entry?: string;
}

/**
 * Expand a route string into legs using the airport's SIDs/STARs. Unknown fixes are kept
 * (the validator reports them); a procedure segment whose entry/exit is missing falls back
 * to the body alone.
 */
export function expandRoute(airport: AirportData, route: string): ExpandedRoute {
  const legs: RouteLeg[] = [];
  let procedure: Procedure | undefined, exit: string | undefined, entry: string | undefined;
  for (const seg of parseRouteString(route)) {
    if (seg.kind === 'direct') { legs.push({ kind: 'fix', fix: seg.fix }); continue; }
    const sid = airport.sids.get(seg.procedure);
    const star = airport.stars.get(seg.procedure);
    const p = sid ?? star;
    if (!p) throw new Error(`Unknown procedure "${seg.procedure}" in route "${route}"`);
    procedure = p; entry = seg.entry; exit = seg.exit;
    if (p.kind === 'sid') {
      legs.push(...(p.rwy[seg.entry] ?? []), ...p.body, ...(p.exitPoints[seg.exit] ?? [{ kind: 'fix', fix: seg.exit }]));
    } else {
      legs.push(...(p.entryPoints[seg.entry] ?? [{ kind: 'fix', fix: seg.entry }]), ...p.body, ...(p.rwy[seg.exit] ?? []));
    }
  }
  // Collapse consecutive duplicates (entry point lists often repeat the entry fix).
  const dedup: RouteLeg[] = [];
  for (const l of legs) {
    const prev = dedup[dedup.length - 1];
    if (prev && prev.kind === 'fix' && l.kind === 'fix' && prev.fix === l.fix) { if (l.restriction) prev.restriction = { ...prev.restriction, ...l.restriction }; continue; }
    dedup.push({ ...l });
  }
  return { legs: dedup, procedure, exit, entry };
}

/** Every fix name a procedure references (for validation and drawing). */
export function procedureFixNames(p: Procedure): Set<string> {
  const names = new Set<string>();
  const add = (legs: RouteLeg[]) => { for (const l of legs) if (l.kind === 'fix') names.add(l.fix); };
  add(p.body);
  for (const legs of Object.values(p.rwy)) add(legs);
  for (const legs of Object.values(p.exitPoints)) add(legs);
  for (const legs of Object.values(p.entryPoints)) add(legs);
  return names;
}
